/* The editor's files besides SKILL.md, which one is open, and what is done to them: add,
   rename or move, remove, write, and upload into the folder. Every change calls
   `onChange` first, so a refused save goes stale as soon as the work moves on. */
import { useState } from "react";
import { toast } from "sonner";

import { isKept, readText, textFile, type DraftFile } from "../folder";

/** Text, as the editor's own file; anything else kept as the bytes it is. */
async function asDraft(file: File): Promise<DraftFile> {
  const text = await readText(file);
  return text != null ? textFile(file.name, text) : { path: file.name, text: null, blob: file, size: file.size };
}

export function useDraftFiles({ start, startOpen, onChange, onSkillMd }: {
  start: DraftFile[];
  startOpen: string;
  onChange: () => void;
  /** A SKILL.md among uploaded files fills the fields: it is never a second file. */
  onSkillMd: (text: string) => Promise<void>;
}) {
  const [files, setFiles] = useState<DraftFile[]>(start);
  const [open, setOpen] = useState(startOpen);
  const edit = (next: (now: DraftFile[]) => DraftFile[]) => {
    onChange();
    setFiles(next);
  };

  const upload = async (list: FileList) => {
    const picked = Array.from(list);
    const left = picked.filter((f) => !isKept(f.name));
    if (left.length) toast(`Left out ${left.map((f) => f.name).join(", ")}: no skill keeps ${left.length === 1 ? "it" : "them"}.`);
    const uploaded = await Promise.all(picked.filter((f) => isKept(f.name)).map(asDraft));
    if (uploaded.some((a) => a.path === "SKILL.md" && a.text == null)) toast.error("That SKILL.md isn't UTF-8 text, so it can't be read: save it as UTF-8 and upload it again.");
    const skillMd = uploaded.find((a) => a.path === "SKILL.md" && a.text != null);
    if (skillMd) await onSkillMd(skillMd.text!);
    const added = uploaded.filter((a) => a.path !== "SKILL.md");
    if (!added.length) return setOpen("SKILL.md");
    const replaced = added.filter((a) => files.some((f) => f.path === a.path)).map((a) => a.path);
    // what was typed while the files were read stays: the list is taken as it is now
    edit((now) => [...now.filter((f) => !added.some((a) => a.path === f.path)), ...added]);
    setOpen(added[0].path);
    if (replaced.length) toast(`Replaced ${replaced.join(", ")}`);
  };

  return {
    files,
    open,
    setOpen,
    add: (path: string) => {
      edit((now) => [...now, textFile(path, "")]);
      setOpen(path);
    },
    rename: (from: string, to: string) => {
      edit((now) => now.map((f) => (f.path === from ? { ...f, path: to } : f)));
      setOpen((now) => (now === from ? to : now));
    },
    remove: (path: string) => {
      edit((now) => now.filter((f) => f.path !== path));
      setOpen((now) => (now === path ? "SKILL.md" : now));
    },
    write: (path: string, text: string) => edit((now) => now.map((f) => (f.path === path ? textFile(path, text) : f))),
    upload,
  };
}
